import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
  redirectTo?: string;
}

export function ProtectedRoute({ 
  children, 
  allowedRoles, 
  redirectTo = '/login' 
}: ProtectedRouteProps) {
  const { user, profile, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-slate-500 font-bold animate-pulse">Authenticating...</p>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    // If we're trying to access a staff route, redirect to staff login
    const isStaffRoute = location.pathname.startsWith('/staff');
    const targetRedirect = isStaffRoute ? '/staff/login' : redirectTo;
    
    return <Navigate to={targetRedirect} state={{ from: location }} replace />;
  }

  // Logged in but role doesn't match
  if (allowedRoles && (!profile || !allowedRoles.includes(profile.role))) {
    // Redirect based on role or to home
    if (profile?.role === 'staff') return <Navigate to="/staff/dashboard" replace />;
    if (profile?.role === 'admin' || profile?.role === 'manager') return <Navigate to="/admin" replace />;
    
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
